"use client";

import { motion } from "framer-motion";
import { fadeInUp, staggerContainer } from "@/lib/animations";
import {
  Download,
  FileText,
  Eye,
  Building2,
  Target,
  Users,
  Award,
} from "lucide-react";
import { useState } from "react";
import Button from "@/components/ui/Button";

const highlights = [
  {
    icon: Building2,
    title: "Our Vision",
    description: "Leading creative hub in the Middle East",
    color: "text-blue-500 dark:text-blue-400",
    bgColor: "bg-blue-500/10",
  },
  {
    icon: Target,
    title: "Our Mission",
    description: "Empowering Saudi designers & creatives",
    color: "text-primary",
    bgColor: "bg-primary/10",
  },
  {
    icon: Users,
    title: "Community",
    description: "500+ active creative professionals",
    color: "text-purple-500 dark:text-purple-400",
    bgColor: "bg-purple-500/10",
  },
  {
    icon: Award,
    title: "Excellence",
    description: "Award-winning workspace & facilities",
    color: "text-orange-500 dark:text-orange-400",
    bgColor: "bg-orange-500/10",
  },
];

export default function CompanyProfile() {
  const [isDownloading, setIsDownloading] = useState(false);

  // Replace with your actual PDF path
  const pdfPath = "/files/company-profile.pdf";

  const handleDownload = () => {
    setIsDownloading(true);

    // Create a temporary link and trigger download
    const link = document.createElement("a");
    link.href = pdfPath;
    link.download = "idworks-hub-company-profile.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Reset download state after animation
    setTimeout(() => {
      setIsDownloading(false);
    }, 2000);
  };

  const handlePreview = () => {
    // Open PDF in new tab for preview
    window.open(pdfPath, "_blank");
  };

  return (
    <section
      id="company-profile"
      className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-8 bg-card/40 relative overflow-hidden"
    >
      {/* Animated Background Elements */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl" />

      <div className="container mx-auto max-w-7xl relative z-10">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {/* Section Header */}
          <motion.div
            variants={fadeInUp}
            className="text-center mb-12 sm:mb-16 lg:mb-20"
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
              className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-primary/10 mb-6"
            >
              <FileText className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
            </motion.div>

            <h2 className="text-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 px-4">
              Company Profile
            </h2>
            <p className="text-text text-base sm:text-lg md:text-xl max-w-3xl mx-auto px-4 leading-relaxed">
              Discover the story behind idworks Hub, our vision, and how we're
              shaping the future of creative spaces in Saudi Arabia
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            {/* Left Side - PDF Preview Card */}
            <motion.div variants={fadeInUp} className="relative">
              <div className="sticky top-24">
                {/* Main PDF Card */}
                <div className="relative group">
                  {/* Decorative Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500" />

                  {/* Card Content */}
                  <div className="relative bg-card/90 backdrop-blur-xl rounded-3xl border border-border shadow-2xl overflow-hidden">
                    {/* PDF Icon/Preview Area */}
                    <div className="relative h-64 sm:h-80 bg-gradient-to-br from-primary/10 to-transparent flex items-center justify-center p-8">
                      <motion.div
                        animate={{
                          y: [0, -10, 0],
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                        className="relative"
                      >
                        {/* PDF Document Illustration */}
                        <div className="w-32 h-40 sm:w-40 sm:h-48 bg-card rounded-2xl shadow-2xl border-4 border-primary/20 flex items-center justify-center relative overflow-hidden">
                          {/* Document lines */}
                          <div className="absolute inset-0 p-6 space-y-3">
                            <div className="h-2 bg-primary/30 rounded-full w-3/4" />
                            <div className="h-2 bg-primary/20 rounded-full w-full" />
                            <div className="h-2 bg-primary/20 rounded-full w-5/6" />
                            <div className="h-2 bg-primary/30 rounded-full w-4/5" />
                            <div className="h-2 bg-primary/20 rounded-full w-full" />
                            <div className="h-2 bg-primary/20 rounded-full w-3/4" />
                          </div>

                          {/* PDF Icon */}
                          <FileText className="w-16 h-16 text-primary relative z-10" />
                        </div>

                        {/* Floating badge */}
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.5, type: "spring" }}
                          className="absolute -top-3 -right-3 bg-primary text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg"
                        >
                          PDF
                        </motion.div>
                      </motion.div>
                    </div>

                    {/* Card Info */}
                    <div className="p-6 sm:p-8">
                      <h3 className="text-heading text-xl sm:text-2xl font-bold mb-2">
                        idworks Hub Profile
                      </h3>
                      <p className="text-text text-sm sm:text-base mb-6">
                        Complete overview of our facilities, services, and
                        success stories
                      </p>

                      {/* File Details */}
                      <div className="flex items-center gap-4 text-sm text-text/70 mb-6 pb-6 border-b border-border">
                        <span className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          PDF Document
                        </span>
                        <span>•</span>
                        <span>12 Pages</span>
                        <span>•</span>
                        <span>2.3 MB</span>
                      </div>

                      {/* Action Buttons */}
                      <div className="grid grid-cols-2 gap-3 sm:gap-4">
                        <Button
                          variant="primary"
                          onClick={handleDownload}
                          className="w-full"
                          //   disabled={isDownloading}
                        >
                          {isDownloading ? (
                            <>
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{
                                  duration: 1,
                                  repeat: Infinity,
                                  ease: "linear",
                                }}
                              >
                                <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                              </motion.div>
                              Downloading...
                            </>
                          ) : (
                            <>
                              <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                              Download
                            </>
                          )}
                        </Button>

                        <Button
                          variant="outline"
                          onClick={handlePreview}
                          className="w-full"
                        >
                          <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                          Preview
                        </Button>
                      </div>
                    </div>

                    {/* Decorative corner accent */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/20 to-transparent rounded-bl-full" />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right Side - Highlights Grid */}
            <div className="space-y-6">
              <motion.div variants={fadeInUp}>
                <h3 className="text-heading text-2xl sm:text-3xl font-bold mb-6 sm:mb-8">
                  What You'll Discover
                </h3>
              </motion.div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {highlights.map((highlight, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    custom={index}
                    className="group relative"
                  >
                    <div className="relative h-full bg-card/80 backdrop-blur-xl rounded-2xl border border-border p-6 hover:shadow-xl transition-all duration-300 overflow-hidden">
                      {/* Hover gradient effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                      {/* Content */}
                      <div className="relative z-10">
                        <div
                          className={`w-12 h-12 rounded-xl ${highlight.bgColor} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}
                        >
                          <highlight.icon
                            className={`w-6 h-6 ${highlight.color}`}
                          />
                        </div>

                        <h4 className="text-heading text-lg font-bold mb-2">
                          {highlight.title}
                        </h4>
                        <p className="text-text text-sm leading-relaxed">
                          {highlight.description}
                        </p>
                      </div>

                      {/* Bottom accent line */}
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary/50 to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-center" />
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Additional Info Card */}
              <motion.div
                variants={fadeInUp}
                className="mt-8 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent rounded-2xl border border-primary/20 p-6 sm:p-8"
              >
                <h4 className="text-heading text-lg sm:text-xl font-bold mb-3">
                  📄 Inside the Profile
                </h4>
                <ul className="space-y-2 text-text text-sm sm:text-base">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">✓</span>
                    <span>Comprehensive facility overview & floor plans</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">✓</span>
                    <span>Membership packages & pricing details</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">✓</span>
                    <span>Success stories from our community</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">✓</span>
                    <span>Upcoming events & programs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">✓</span>
                    <span>Partnership opportunities</span>
                  </li>
                </ul>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
